import React, { useEffect, useState, useCallback } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./orders.module.css";

const naira = (v) => `₦${new Intl.NumberFormat("en-NG").format(v || 0)}`;

const STATUS_LABELS = {
  pending: "Pending",
  "payment-reported": "Payment reported",
  paid: "Paid",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
};

const chipClass = (styles, status) =>
  ({
    pending: styles.chipPending,
    "payment-reported": styles.chipReported,
    paid: styles.chipPaid,
    fulfilled: styles.chipFulfilled,
    cancelled: styles.chipCancelled,
  }[status] || styles.chipPending);

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canManage = canManageProducts();

  const fetchOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/orders`, { headers: authHeader() });
      if (!response.ok) throw new Error("Could not load orders.");
      setOrders(await response.json());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const setStatus = async (order, status) => {
    setError("");
    try {
      const response = await fetch(`${API_URL}/orders/${order._id || order.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Could not update the order.");
      }
      fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Orders</h1>
          <p className={styles.subtitle}>
            Every checkout, with its payment status. “Payment reported” means the
            shopper finished Paystack but the charge hasn’t been verified with
            Paystack’s API — confirm it in your Paystack dashboard before shipping.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}

        {loading ? (
          <p className={styles.loading}>Loading orders…</p>
        ) : orders.length === 0 ? (
          <p className={styles.empty}>No orders yet.</p>
        ) : (
          orders.map((order) => {
            const id = order._id || order.id;
            return (
              <article key={id} className={styles.card}>
                <div className={styles.cardTop}>
                  <span className={chipClass(styles, order.status)}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                  {order.paymentVerified && (
                    <span className={styles.verified}>✓ Verified with Paystack</span>
                  )}
                  <span className={styles.orderId}>#{id.slice(-6).toUpperCase()}</span>
                  <span className={styles.date}>
                    {new Date(order.dateCreated).toLocaleString()}
                  </span>
                </div>

                <div>
                  <div className={styles.customer}>
                    {order.customer?.fullName || order.customer?.email}
                  </div>
                  <div className={styles.customerMeta}>
                    {[
                      order.customer?.email,
                      order.customer?.phone,
                      order.customer?.address,
                      order.customer?.location,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>

                <ul className={styles.items}>
                  {order.orderItems.map((item, i) => (
                    <li key={i} className={styles.item}>
                      <span>
                        {item.name}
                        {item.color ? ` · ${item.color}` : ""} × {item.quantity}
                      </span>
                      <span>{naira(item.price * item.quantity)}</span>
                    </li>
                  ))}
                </ul>

                <div className={styles.totals}>
                  <span>Subtotal {naira(order.subtotal)}</span>
                  {order.discountAmount > 0 && (
                    <span>
                      Discount ({order.discountCode}) −{naira(order.discountAmount)}
                    </span>
                  )}
                  <span>Delivery {naira(order.deliveryFee)}</span>
                  <span className={styles.grand}>Total {naira(order.total)}</span>
                </div>

                {canManage && (
                  <div className={styles.actions}>
                    {(order.status === "paid" || order.status === "payment-reported") && (
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => setStatus(order, "fulfilled")}
                      >
                        Mark fulfilled
                      </button>
                    )}
                    {order.status === "payment-reported" && (
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => setStatus(order, "paid")}
                      >
                        Confirm payment
                      </button>
                    )}
                    {order.status !== "cancelled" && order.status !== "fulfilled" && (
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        onClick={() => setStatus(order, "cancelled")}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Orders;
